// src/booking/PublicBookingRouter.jsx
//
// Thin router that sits at /book/:slug.
// Resolves whether the slug belongs to a Pro account or a Solo account,
// then renders the appropriate booking page component.
//
// Resolution logic:
//   - Call GET /api/pro-booking/resolve-slug?slug=<slug>
//   - 200 → Pro account with online booking enabled → render ProBookingPage
//   - 404 → not a Pro slug → fall through to Solo BookingPage
//   - network error → fall through to Solo BookingPage (fail open)
//
// Both child components receive the slug via useParams() so no props needed.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import BookingPage from './PublicBookingPage';
import ProBookingPage from './ProBookingPage';

export default function PublicBookingRouter() {
  const { slug } = useParams();
  const [accountType, setAccountType] = useState(null); // 'pro' | 'solo'

  useEffect(() => {
    if (!slug) {
      setAccountType('solo');
      return;
    }

    fetch(`/api/pro-booking/resolve-slug?slug=${encodeURIComponent(slug)}`)
      .then((res) => {
        if (res.ok) {
          setAccountType('pro');
        } else {
          setAccountType('solo');
        }
      })
      .catch(() => {
        // Network error — fall through to Solo (fail open)
        setAccountType('solo');
      });
  }, [slug]);

  // Show nothing while resolving — both child components have their own
  // loading states so there's no flash of wrong content.
  if (!accountType) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f8fafc',
      }}>
        <p style={{ color: '#64748b', fontFamily: 'Inter, sans-serif' }}>
          Loading…
        </p>
      </div>
    );
  }

  return accountType === 'pro' ? <ProBookingPage /> : <BookingPage />;
}